/** THE PLAYER BOARD — every player on tonight's board, live.
 *
 *  Opened by the "Live players" button in the black bar, inside our own page:
 *  the fan never leaves. It reads its OWN player-facing feed, which carries
 *  only what is drawn here — screen name, establishment, winner pick, star of
 *  the night, points bank, wins and "% right". No email address and no staff
 *  name is in that response at all.
 *
 *  THREE TABS: Players, Leaderboard, and — only where a super admin has
 *  switched "Live chat" on — Chat. Tapping any name opens the small profile.
 *
 *  LOOK: "Stadium Dark" by default; every colour, logo, wallpaper, watermark
 *  and banner saved in Board design still overrides it, through boardSkin.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Crown, Home, MessageCircle, User, X } from 'lucide-react';
import { GoalBoardChat } from './GoalBoardChat';
import { GoalPlayerProfile } from './GoalPlayerProfile';
import { boardAbs, boardSkin } from '../../lib/boardSkin';
import { GoalTapelligenceFooter } from './GoalTapelligenceFooter';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game`;
const EVERY = 10000;

/** 50% and over green, under 50% red, fewer than three picks grey "New". */
const RightCell = ({ value, t }) => {
    if (value === null || value === undefined) {
        return (
            <span className="text-xs font-semibold opacity-50" data-testid="lp-right-new">
                {t('goalGame.boardNew')}
            </span>
        );
    }
    return (
        <span className={`text-xs font-bold tabular-nums ${value >= 50 ? 'text-emerald-400' : 'text-red-400'}`}
              data-testid="lp-right-value">
            {value}%
        </span>
    );
};

const Points = ({ value, skin }) => (
    <span className="shrink-0 rounded-lg border px-2 py-0.5 text-xs font-bold tabular-nums"
          style={{ color: skin.gold, borderColor: `${skin.gold}66` }}
          data-testid="lp-points-badge">
        {value} pts
    </span>
);

const PlayerRow = ({ p, t, onOpen, skin, pointsOn = true }) => (
    <div className="rounded-2xl border px-3 py-2.5" data-testid="lp-player-row"
         style={{ backgroundColor: skin.card, borderColor: skin.border }}>
        <div className="flex items-center justify-between gap-3">
            <button type="button" onClick={() => onOpen(p)} data-testid="lp-player-name"
                    className="truncate text-left text-sm font-bold underline-offset-2 hover:underline">
                {p.player}
            </button>
            {pointsOn && <Points value={p.points} skin={skin} />}
        </div>
        <p className="text-[11px] truncate" style={{ color: skin.meta }}>
            {p.establishment || t('goalGame.boardAtHome')}
        </p>
        <div className="mt-1 flex items-end justify-between gap-3">
            <div className="grid min-w-0 grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]"
                 style={{ color: skin.meta }}>
                <span>{t('goalGame.boardPick')}: {p.winner_pick || '—'}</span>
                <span className="truncate">{t('goalGame.boardStar')}: {p.star_pick || '—'}</span>
                {pointsOn && (
                    <span className="tabular-nums">{t('goalGame.boardPoints')}: {p.points}</span>
                )}
                <span className="tabular-nums">{t('goalGame.boardWins')}: {p.wins}</span>
            </div>
            {/* The % needs to say what it is, in the profile's own words. */}
            <span className="flex shrink-0 flex-col items-end leading-tight">
                <span className="text-[10px] uppercase tracking-wide"
                      style={{ color: skin.meta }} data-testid="lp-right-label">
                    {t('goalGame.profileAccuracy')}
                </span>
                <RightCell value={p.right_percent} t={t} />
            </span>
        </div>
    </div>
);

/** Top three, tallest in the middle. Fewer than three players shows only who
 *  is really there — no empty plinths. */
const Podium = ({ top, figure, onOpen, skin }) => {
    const order = [top[1], top[0], top[2]].filter(Boolean);
    const medal = (p) => (p === top[0] ? skin.gold : p === top[1] ? skin.silver : skin.bronze);
    const place = (p) => (p === top[0] ? 1 : p === top[1] ? 2 : 3);
    return (
        <div className="mb-4 flex items-end justify-center gap-2" data-testid="lp-podium">
            {order.map((p) => (
                <button key={p.key} type="button" onClick={() => onOpen(p.key)}
                        data-testid={`lp-podium-${place(p)}`}
                        className={`relative flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl border px-2 pb-2 ${
                            place(p) === 1 ? 'pt-7' : 'pt-4'}`}
                        style={{ backgroundColor: skin.cardHigh, borderColor: medal(p),
                                 maxWidth: '34%' }}>
                    {place(p) === 1 && (
                        <Crown size={18} className="absolute -top-2.5" style={{ color: skin.gold }} />
                    )}
                    <span className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-black"
                          style={{ backgroundColor: medal(p), color: '#0E1117' }}>
                        {place(p)}
                    </span>
                    <span className="w-full truncate text-center text-xs font-bold">{p.player}</span>
                    <span className="text-sm font-black tabular-nums" style={{ color: medal(p) }}
                          data-testid="lp-podium-figure">{figure(p)}</span>
                </button>
            ))}
        </div>
    );
};

export const GoalLivePlayersBoard = ({ cardId, t, brand, c, ty, logoUrl, onClose,
                                       framed = false, lang = 'en', startTab = 'players' }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(startTab);
    const [openKey, setOpenKey] = useState('');
    const [myName, setMyName] = useState('');
    const skin = useMemo(() => boardSkin(brand, c, ty, lang, logoUrl),
                         [brand, c, ty, lang, logoUrl]);

    const load = useCallback(async () => {
        const device = typeof window !== 'undefined'
            ? localStorage.getItem('patron_device_id') || '' : '';
        try {
            const res = await fetch(`${API}/public/${cardId}/live-players`
                + `?device_id=${encodeURIComponent(device)}`);
            setData(res.ok ? await res.json() : { enabled: false, players: [] });
        } catch (_e) {
            setData((prev) => prev || { enabled: false, players: [] });
        } finally { setLoading(false); }
    }, [cardId]);

    useEffect(() => {
        load();
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, EVERY);
        return () => clearInterval(id);
    }, [load]);

    // Who this phone is, for the "You" row and the Profile button. Read from
    // the Patron Points card that is already on this device — no new feed.
    useEffect(() => {
        const device = typeof window !== 'undefined'
            ? localStorage.getItem('patron_device_id') || '' : '';
        if (!device) return;
        let gone = false;
        (async () => {
            try {
                const res = await fetch(
                    `${window.location.origin}/api/patron/public/${cardId}/me`
                    + `?device_id=${encodeURIComponent(device)}&lang=${lang}`);
                if (!res.ok) return;
                const me = await res.json();
                if (!gone) setMyName(String(me?.screen_name || ''));
            } catch (_e) { /* no name = no You row, nothing else changes */ }
        })();
        return () => { gone = true; };
    }, [cardId, lang]);

    const players = useMemo(() => data?.players || [], [data]);
    const establishments = data?.establishments || [];
    const chatOn = Boolean(data?.chat);
    // POINTS ARE THE OPTIONAL PROGRAMME. A venue that does not run Patriot
    // Points shows no points anywhere on this board.
    const pointsOn = data?.points_on !== false;
    const mine = data?.my_establishment || '';
    const [here, setHere] = useState(false);
    const [rank, setRank] = useState('points');
    // With points off, the leaderboard ranks by wins instead.
    const rankBy = (!pointsOn && rank === 'points') ? 'wins' : rank;
    const [period, setPeriod] = useState('tonight');

    const shown = useMemo(() => {
        const list = here && mine
            ? players.filter((p) => p.establishment === mine)
            : players;
        const value = (p) => {
            if (rankBy === 'right') {
                const v = period === 'tonight' ? p.right_percent_tonight : p.right_percent;
                return v === null || v === undefined ? -1 : v;
            }
            if (rankBy === 'wins') return period === 'tonight' ? p.wins : p.wins_all;
            return period === 'tonight' ? p.points_tonight : p.points;
        };
        return [...list].sort((a, b) => value(b) - value(a)
            || a.player.toLowerCase().localeCompare(b.player.toLowerCase()));
    }, [players, here, mine, rankBy, period]);

    const figure = (p) => {
        if (rankBy === 'right') {
            const v = period === 'tonight' ? p.right_percent_tonight : p.right_percent;
            return v === null || v === undefined ? t('goalGame.boardNew') : `${v}%`;
        }
        if (rankBy === 'wins') return period === 'tonight' ? p.wins : p.wins_all;
        return period === 'tonight' ? p.points_tonight : p.points;
    };

    const open = players.find((p) => p.key === openKey) || null;
    const myKey = data?.me?.key || '';
    const meIndex = shown.findIndex((p) => (myKey ? p.key === myKey
        : Boolean(myName) && p.player === myName));
    const meRow = meIndex >= 0 ? shown[meIndex] : null;

    // The Chat tab disappears the moment the switch goes off.
    useEffect(() => {
        if (!chatOn && tab === 'chat') setTab('players');
    }, [chatOn, tab]);

    const tabs = [
        ['players', t('goalGame.tabPlayers')],
        ['leaderboard', t('goalGame.tabLeaderboard')],
        ...(chatOn ? [['chat', t('goalGame.tabChat')]] : []),
    ];

    const nav = [
        ['home', t('goalGame.navHome'), Home, () => onClose?.()],
        ['leaderboard', t('goalGame.navLeaderboard'), BarChart3, () => setTab('leaderboard')],
        ...(chatOn ? [['chat', t('goalGame.navChat'), MessageCircle, () => setTab('chat')]] : []),
        ['profile', t('goalGame.navProfile'), User,
            () => meRow && setOpenKey(meRow.key)],
    ];

    const chip = (on) => ({
        backgroundColor: on ? skin.accent : 'transparent',
        color: on ? '#ffffff' : skin.meta,
        borderColor: on ? skin.accent : skin.border,
    });

    return (
        <div className={`${framed ? 'absolute' : 'fixed'} inset-0 z-40 flex items-end`}
             data-testid="lp-panel-wrap">
            <button type="button" aria-label="close" onClick={onClose} data-testid="lp-scrim"
                    className="absolute inset-0 bg-black/70" />
            <div className="relative w-full max-h-[88%] overflow-y-auto rounded-t-3xl border-t px-4 pb-0 pt-0 sw-rise"
                 style={{ backgroundColor: skin.bg, color: skin.text,
                          borderColor: skin.border, ...skin.bodyStyle }}
                 data-testid="lp-panel">
                {/* The owner's wallpaper, with its own darkening so names stay
                    readable on a bright photo. */}
                {skin.wallpaperUrl && (
                    <div className="pointer-events-none absolute inset-0 rounded-t-3xl bg-cover bg-center"
                         style={{ backgroundImage: `url("${boardAbs(skin.wallpaperUrl)}")` }}
                         data-testid="lp-wallpaper">
                        <div className="absolute inset-0 rounded-t-3xl"
                             style={{ backgroundColor: skin.bg,
                                      opacity: skin.wallpaperOverlay / 100 }} />
                    </div>
                )}

                {/* The watermark sits BEHIND everything and is never tappable. */}
                {(skin.watermarkUrl || skin.watermarkText) && (
                    <div className={`pointer-events-none absolute flex ${
                        skin.watermarkCorner ? 'bottom-4 right-4' : 'inset-0 items-center justify-center'}`}
                         style={{ opacity: skin.watermarkOpacity }} data-testid="lp-watermark">
                        {skin.watermarkUrl ? (
                            <img src={boardAbs(skin.watermarkUrl)} alt=""
                                 style={{ width: `${skin.watermarkSize}%`, maxWidth: '85%' }}
                                 className="object-contain" />
                        ) : (
                            <span className="font-black uppercase tracking-widest"
                                  style={{ fontSize: `${Math.max(14, skin.watermarkSize / 2)}px` }}>
                                {skin.watermarkText}
                            </span>
                        )}
                    </div>
                )}

                <div className="relative">
                {/* HEADER BAND — the crowd only lives here, behind whichever
                    logos the owner has set. With both slots empty it is just
                    the title; nothing is drawn behind a logo. */}
                <div className="relative -mx-4 overflow-hidden rounded-t-3xl px-4 pb-3 pt-4"
                     data-testid="lp-header">
                    {!skin.wallpaperUrl && (
                        <div className="pointer-events-none absolute inset-0">
                            <div className="absolute inset-0 bg-cover bg-center opacity-60"
                                 style={{ backgroundImage: `url("${skin.headerImage}")` }}
                                 data-testid="lp-header-crowd" />
                            <div className="absolute inset-0"
                                 style={{ backgroundImage:
                                     `linear-gradient(180deg, ${skin.bg}99 0%, ${skin.bg} 100%)` }} />
                        </div>
                    )}
                    <div className="relative flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                            {skin.logoUrl ? (
                                <img src={boardAbs(skin.logoUrl)} alt={brand?.organization_name || ''}
                                     data-testid="lp-panel-logo"
                                     style={{ height: `${skin.logoSize || Math.min(brand?.logo_size_px || 40, 48)}px` }}
                                     className="object-contain" />
                            ) : null}
                            {skin.logo2Url && (
                                <img src={boardAbs(skin.logo2Url)} alt="" data-testid="lp-panel-logo2"
                                     style={{ height: `${skin.logo2Size || 32}px` }}
                                     className="object-contain" />
                            )}
                        </div>
                        <button type="button" onClick={onClose} data-testid="lp-close"
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border"
                                style={{ borderColor: skin.border }}>
                            <X size={16} />
                        </button>
                    </div>
                    <p className="relative mt-2 text-lg font-bold" style={skin.headlineStyle}
                       data-testid="lp-panel-title">
                        {t('goalGame.boardTitle')}
                    </p>
                </div>

                {/* PINNED — the manager's announcement sits above every tab.
                    A player can never send, edit or remove one. */}
                {(data?.announcements || []).map((a) => (
                    <div key={a.id} className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2"
                         style={{ backgroundColor: skin.bannerBg || skin.accent,
                                  color: skin.bannerText || '#ffffff' }}
                         data-testid="lp-announcement">
                        {skin.bannerImage && (
                            <img src={boardAbs(skin.bannerImage)} alt=""
                                 className="h-6 shrink-0 object-contain" />
                        )}
                        <span className="font-bold leading-snug"
                              style={skin.bannerSize ? undefined : { fontSize: '13px' }}>
                            {a.text?.[lang] || a.text?.en}
                        </span>
                    </div>
                ))}

                <div className="mb-4 flex items-center gap-1.5" data-testid="lp-tabs">
                    {tabs.map(([key, label]) => (
                        <button key={key} type="button" onClick={() => setTab(key)}
                                data-testid={`lp-tab-${key}`}
                                className="rounded-full border px-4 py-2 text-xs font-semibold transition-colors"
                                style={chip(tab === key)}>
                            {label}
                        </button>
                    ))}
                </div>

                {/* "My location only" — one tap narrows every tab to this
                    table's own establishment, and the same tap widens it. */}
                {tab !== 'chat' && mine && establishments.length > 1 && (
                    <button type="button" onClick={() => setHere((v) => !v)}
                            data-testid="lp-chip-here"
                            className="mb-3 rounded-full border px-4 py-2 text-xs font-semibold"
                            style={chip(here)}>
                        {t('goalGame.boardMyLocation')}
                    </button>
                )}

                {/* Each establishment's own total, so competing venues can see
                    where they stand. */}
                {tab !== 'chat' && establishments.length > 1 && (
                    <div className="mb-4 space-y-1" data-testid="lp-establishments">
                        {establishments
                            .filter((e) => !here || !mine || e.establishment === mine)
                            .map((e) => (
                            <div key={e.establishment}
                                 className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2"
                                 style={{ backgroundColor: skin.card, borderColor: skin.border }}
                                 data-testid="lp-establishment-row">
                                <span className="text-sm font-semibold truncate">{e.establishment}</span>
                                <span className="text-xs tabular-nums" style={{ color: skin.meta }}>
                                    {pointsOn
                                        ? t('goalGame.boardVenueTotals', { players: e.players, points: e.points })
                                        : t('goalGame.boardVenuePlayers', { players: e.players })}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* The leaderboard's own chips: what it is ranked by, and over
                    what. Tonight is the one it opens on. */}
                {tab === 'leaderboard' && (
                    <div className="mb-4 space-y-2" data-testid="lp-rank-chips">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {[...(pointsOn ? [['points', t('goalGame.boardPoints')]] : []),
                              ['right', t('goalGame.profileAccuracy')],
                              ['wins', t('goalGame.boardWins')]].map(([key, label]) => (
                                <button key={key} type="button" onClick={() => setRank(key)}
                                        data-testid={`lp-rank-${key}`}
                                        className="rounded-full border px-3.5 py-2 text-xs font-semibold"
                                        style={chip(rankBy === key)}>
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {[['tonight', t('goalGame.boardTonight')],
                              ['all', t('goalGame.boardAllTime')]].map(([key, label]) => (
                                <button key={key} type="button" onClick={() => setPeriod(key)}
                                        data-testid={`lp-period-${key}`}
                                        className="rounded-full border px-3.5 py-2 text-xs font-semibold"
                                        style={chip(period === key)}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {tab === 'chat' ? (
                    <GoalBoardChat cardId={cardId} t={t} c={{ ...c, bg: skin.bg, text: skin.text,
                                                              accent: skin.accent }}
                                   ty={{ body: skin.bodyStyle, headline: skin.headlineStyle }}
                                   skin={skin}
                                   onOpenPlayer={(key) => setOpenKey(key)} />
                ) : loading && !data ? (
                    <p className="py-6 text-center text-sm" style={{ color: skin.meta }}
                       data-testid="lp-loading">…</p>
                ) : shown.length === 0 ? (
                    <p className="py-6 text-center text-sm" style={{ color: skin.meta }}
                       data-testid="lp-empty">
                        {t('goalGame.boardEmpty')}
                    </p>
                ) : tab === 'leaderboard' ? (
                    <div data-testid="lp-leaderboard">
                        <Podium top={shown.slice(0, 3)} figure={figure} skin={skin}
                                onOpen={(key) => setOpenKey(key)} />
                        <div className="space-y-1.5">
                            {shown.slice(3).map((p, i) => (
                                <button key={`${p.key}-${i}`} type="button"
                                        onClick={() => setOpenKey(p.key)} data-testid="lp-leader-row"
                                        style={{ backgroundColor: skin.card, borderColor: skin.border }}
                                        className="flex w-full items-center gap-3 rounded-2xl border px-3 py-2.5 text-left">
                                    <span className="w-7 shrink-0 text-sm font-black tabular-nums"
                                          style={{ color: skin.gold }}>
                                        #{i + 4}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-bold">{p.player}</span>
                                        <span className="block truncate text-[11px]"
                                              style={{ color: skin.meta }}>
                                            {p.establishment || t('goalGame.boardAtHome')}
                                        </span>
                                    </span>
                                    <span className="shrink-0 text-sm font-bold tabular-nums"
                                          style={{ color: skin.gold }}
                                          data-testid="lp-leader-figure">{figure(p)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="lp-players">
                        {shown.map((p, i) => (
                            <PlayerRow key={`${p.key}-${i}`} p={p} t={t} skin={skin}
                                       pointsOn={pointsOn}
                                       onOpen={(row) => setOpenKey(row.key)} />
                        ))}
                    </div>
                )}

                {/* YOU — only for a signed-in player who actually has a rank. */}
                {tab === 'leaderboard' && meRow && (
                    <div className="sticky bottom-[68px] z-10 mt-3 flex items-center justify-between gap-3 rounded-2xl border-2 px-3 py-2.5"
                         style={{ backgroundColor: skin.cardHigh, borderColor: skin.gold }}
                         data-testid="lp-you-row">
                        <span className="truncate text-sm font-bold">
                            {t('goalGame.boardYouRank', { player: meRow.player, rank: meIndex + 1 })}
                        </span>
                        <span className="shrink-0 text-sm font-black tabular-nums"
                              style={{ color: skin.gold }} data-testid="lp-you-figure">
                            {figure(meRow)}
                        </span>
                    </div>
                )}

                {/* BOTTOM NAV — inside the panel, above everything that scrolls. */}
                <GoalTapelligenceFooter />
                <div className="sticky bottom-0 z-20 -mx-4 mt-3 flex items-stretch border-t px-2 pb-2 pt-1"
                     style={{ backgroundColor: skin.bg, borderColor: skin.border }}
                     data-testid="lp-nav">
                    {nav.map(([key, label, Icon, act]) => {
                        const on = key === tab || (key === 'home' && false);
                        return (
                            <button key={key} type="button" onClick={act}
                                    data-testid={`lp-nav-${key}`}
                                    className="flex min-h-[44px] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-1"
                                    style={{ color: on ? skin.accent : skin.meta }}>
                                <Icon size={18} />
                                <span className="text-[10px] font-semibold">{label}</span>
                            </button>
                        );
                    })}
                </div>

                <GoalPlayerProfile player={open} t={t} pointsOn={pointsOn}
                                   c={{ ...c, bg: skin.bg, text: skin.text }}
                                   ty={{ body: skin.bodyStyle, headline: skin.headlineStyle }}
                                   onClose={() => setOpenKey('')} />
                </div>
            </div>
        </div>
    );
};

export default GoalLivePlayersBoard;
